import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { ProductPicker } from '../../components/ui/ProductPicker.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { salesApi } from '../../services/salesApi.js';
import { addDaysIso, formatCurrency, todayIso } from '../../utils/formatters.js';
import { unitShort } from '../../utils/units.js';
import { computeInvoiceTotals } from '../../utils/vat.js';
import { ProductCreateModal } from '../inventory/ProductCreateModal.jsx';

import { CustomerCreateModal } from './CustomerCreateModal.jsx';
import { usePriceResolver } from './usePriceResolver.js';

// Swiss VAT rates: Normalsatz, reduzierter Satz, Beherbergung, befreit.
const TAX_RATES = ['8.1', '2.6', '3.8', '0'];

function emptyLine() {
  return { productId: '', description: '', quantity: '1', unit: '', unitPrice: '0', taxRate: '8.1', priceHint: null };
}

function emptyForm() {
  return {
    customerId: '',
    quoteDate: todayIso(),
    validUntil: addDaysIso(30),
    notes: '',
    lines: [emptyLine()],
  };
}

function formFromQuote(quote) {
  return {
    customerId: quote.customerId || '',
    quoteDate: String(quote.quoteDate || '').slice(0, 10),
    validUntil: String(quote.validUntil || '').slice(0, 10),
    notes: quote.notes || '',
    lines: (quote.lines || []).map((l) => ({
      productId: l.productId,
      description: l.description,
      quantity: String(Number(l.quantity)),
      unit: l.unit || '',
      unitPrice: String(Number(l.unitPrice)),
      taxRate: String(Number(l.taxRate ?? 0)),
      priceHint: null,
    })),
  };
}

/**
 * Create modal doubling as the DRAFT editor: pass `quote` (detail incl.
 * lines) and the form prefills and submits via PUT instead of POST.
 */
export function QuoteCreateModal({ open, onClose, quote }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = Boolean(quote);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  // Line index whose "+ Neu" opened the product quick-create, null = closed.
  const [productModalLine, setProductModalLine] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(quote ? formFromQuote(quote) : emptyForm());
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quote?.id]);

  const customers = useQuery({
    queryKey: ['sales', 'customers'],
    queryFn: () => salesApi.listCustomers().then((r) => r.items || []),
    enabled: open,
  });
  const products = useQuery({
    queryKey: ['inventory', 'products'],
    queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items || []),
    enabled: open,
  });

  // Identical cents arithmetic to the backend (vat.js) - preview matches the
  // saved quote to the Rappen.
  const totals = computeInvoiceTotals(form.lines);

  const save = useMutation({
    mutationFn: (body) => (isEdit ? salesApi.updateQuote(quote.id, body) : salesApi.createQuote(body)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'quotes'] });
      setForm(emptyForm());
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  const { resolveLine, clearHints, invalidate } = usePriceResolver(setForm);

  const patchField = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  const patchLine = (idx, key, value) =>
    setForm((p) => ({
      ...p,
      lines: p.lines.map((line, i) => (i === idx ? { ...line, [key]: value } : line)),
    }));
  const addLine = () => setForm((p) => ({ ...p, lines: [...p.lines, emptyLine()] }));
  const removeLine = (idx) => {
    invalidate(); // line indices shift - drop in-flight price resolutions
    setForm((p) => ({
      ...p,
      lines: p.lines.length > 1 ? p.lines.filter((_, i) => i !== idx) : p.lines,
    }));
  };

  // Shared by picker selection and inline product creation - the fresh
  // product is not in products.data yet, so always work from the object.
  function applyProduct(idx, product) {
    setForm((p) => ({
      ...p,
      lines: p.lines.map((line, i) =>
        i === idx
          ? {
              ...line,
              productId: product.id,
              description: `${product.sku} - ${product.name}`,
              unit: product.unitOfMeasure || '',
              unitPrice: String(product.listPrice),
              priceHint: null,
            }
          : line,
      ),
    }));
    resolveLine(idx, { customerId: form.customerId, productId: product.id, quantity: form.lines[idx]?.quantity });
  }

  function handleProductPick(idx, productId) {
    const product = (products.data || []).find((p) => p.id === productId);
    if (product) applyProduct(idx, product);
  }

  function handleQuantityChange(idx, value) {
    patchLine(idx, 'quantity', value);
    resolveLine(idx, { customerId: form.customerId, productId: form.lines[idx]?.productId, quantity: value });
  }

  function handleCustomerChange(customerId) {
    patchField('customerId', customerId);
    clearHints();
    form.lines.forEach((line, idx) => {
      if (line.productId) {
        resolveLine(idx, { customerId, productId: line.productId, quantity: line.quantity });
      }
    });
  }

  function submit(e) {
    e.preventDefault();
    setError(null);
    // The picker is no native <select required>, so guard here.
    if (form.lines.some((l) => !l.productId)) {
      setError(t('picker.product.required'));
      return;
    }
    save.mutate({
      customerId: form.customerId,
      quoteDate: form.quoteDate,
      validUntil: form.validUntil,
      notes: form.notes || undefined,
      lines: form.lines.map((l) => ({
        productId: l.productId,
        description: l.description.trim() || ' ',
        quantity: Number(l.quantity || 0),
        unit: l.unit || undefined,
        unitPrice: Number(l.unitPrice || 0),
        taxRate: Number(l.taxRate || 0),
      })),
    });
  }

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? t('sales.quotes.modal.editTitle') : t('sales.quotes.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? `${t('common.save')}…` : isEdit ? t('common.save') : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <label className="field">
          <span className="field-label">{t('sales.quotes.modal.customer')}</span>
          <div className="inline-create">
            <select className="select" value={form.customerId} onChange={(e) => handleCustomerChange(e.target.value)} required>
              <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
              {(customers.data || []).map((c) => (
                <option key={c.id} value={c.id}>{c.code} - {c.name}</option>
              ))}
            </select>
            <Button type="button" variant="ghost" className="inline-create-btn" onClick={() => setCustomerModalOpen(true)}>
              + {t('common.new')}
            </Button>
          </div>
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('sales.quotes.modal.quoteDate')}</span>
            <input className="input" type="date" value={form.quoteDate} onChange={(e) => patchField('quoteDate', e.target.value)} required />
          </label>
          <label className="field">
            <span className="field-label">{t('sales.quotes.modal.validUntil')}</span>
            <input className="input" type="date" value={form.validUntil} onChange={(e) => patchField('validUntil', e.target.value)} required />
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span className="field-label">{t('sales.quotes.modal.lines')}</span>
          <Button size="sm" variant="ghost" onClick={addLine}>+ {t('finance.invoices.modal.addLine')}</Button>
        </div>
        {form.lines.map((line, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
              padding: 'var(--space-3)', border: '1px solid var(--color-hairline)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <div className="field">
              <span className="field-label">{t('sales.quotes.modal.product')}</span>
              <div className="inline-create">
                <ProductPicker
                  products={products.data || []}
                  value={line.productId}
                  valueLabel={line.description}
                  onPick={(id) => handleProductPick(idx, id)}
                  onCreateNew={() => setProductModalLine(idx)}
                />
                <Button type="button" variant="ghost" className="inline-create-btn" onClick={() => setProductModalLine(idx)}>
                  + {t('common.new')}
                </Button>
              </div>
            </div>
            <label className="field">
              <span className="field-label">{t('sales.quotes.modal.description')}</span>
              <input className="input" value={line.description} onChange={(e) => patchLine(idx, 'description', e.target.value)} required />
            </label>
            <div className="field-row">
              <label className="field">
                <span className="field-label">
                  {t('sales.quotes.modal.quantity')}{line.unit ? ` (${unitShort(t, line.unit)})` : ''}
                </span>
                <input
                  className="input"
                  type="number"
                  min="0.0001"
                  step="any"
                  value={line.quantity}
                  onChange={(e) => handleQuantityChange(idx, e.target.value)}
                  required
                />
              </label>
              <label className="field">
                <span className="field-label">{t('sales.quotes.modal.unitPrice')}</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.unitPrice}
                  onChange={(e) => patchLine(idx, 'unitPrice', e.target.value)}
                  required
                />
              </label>
              <label className="field" style={{ flex: '0 0 90px' }}>
                <span className="field-label">{t('vat.rateCol')}</span>
                <select
                  className="select"
                  value={line.taxRate}
                  onChange={(e) => patchLine(idx, 'taxRate', e.target.value)}
                >
                  {TAX_RATES.map((rate) => (
                    <option key={rate} value={rate}>{rate} %</option>
                  ))}
                </select>
              </label>
            </div>
            {line.priceHint && (
              <span className="muted text-sm">
                {t('priceList.applied', { name: line.priceHint.name, minQuantity: line.priceHint.minQuantity })}
              </span>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="muted text-sm">
                {t('sales.quotes.modal.lineTotal')}: <strong>{formatCurrency(totals.lineNets[idx])}</strong>
              </span>
              {form.lines.length > 1 && (
                <Button size="sm" variant="danger" onClick={() => removeLine(idx)}>
                  {t('finance.invoices.modal.removeLine')}
                </Button>
              )}
            </div>
          </div>
        ))}

        <div style={{
          display: 'flex', flexDirection: 'column', gap: 'var(--space-1)',
          borderTop: '1px solid var(--color-hairline)', paddingTop: 'var(--space-3)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }} className="muted text-sm">
            <span>{t('vat.net')}</span>
            <span className="mono">{formatCurrency(totals.net)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }} className="muted text-sm">
            <span>{t('vat.tax')}</span>
            <span className="mono">{formatCurrency(totals.tax)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{t('sales.quotes.modal.quoteTotal')}</span>
            <strong className="mono">{formatCurrency(totals.gross)}</strong>
          </div>
        </div>

        <label className="field">
          <span className="field-label">{t('sales.quotes.modal.notes')}</span>
          <textarea className="textarea" rows={2} value={form.notes} onChange={(e) => patchField('notes', e.target.value)} />
        </label>

        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
    <CustomerCreateModal
      open={customerModalOpen}
      onClose={() => setCustomerModalOpen(false)}
      onCreated={(created) => {
        queryClient.invalidateQueries({ queryKey: ['sales', 'customers'] });
        if (created?.id) {
          patchField('customerId', created.id);
        }
      }}
    />
    <ProductCreateModal
      open={productModalLine != null}
      onClose={() => setProductModalLine(null)}
      onCreated={(created) => {
        if (created?.id && productModalLine != null) {
          applyProduct(productModalLine, created);
        }
      }}
    />
    </>
  );
}
